export type DocumentLineItem = {
    name: string;
    description?: string;
    quantity: number;
    price: number;
    discount?: number;
};

export type DocumentDepositInput = {
    enabled?: boolean;
    mode?: 'fixed' | 'percentage';
    value?: number;
};

export type DocumentDeposit = {
    enabled: boolean;
    mode: 'fixed' | 'percentage';
    value: number;
    amount: number;
};

export type DocumentInstallment = {
    label: string;
    dueDate?: string;
    amount: number;
    percentage?: number;
    status?: string;
};

export type DocumentPaymentPlan<TInstallment extends DocumentInstallment = DocumentInstallment> = {
    enabled: boolean;
    installments: TInstallment[];
};

export function toDocumentNumber(value: unknown): number {
    const num = Number(value || 0);
    return Number.isFinite(num) ? num : 0;
}

export function normalizeDocumentLineItem<TLineItem extends DocumentLineItem>(
    item: Partial<TLineItem>,
): TLineItem {
    return {
        name: String(item.name || '').trim() || 'Line item',
        description: item.description ? String(item.description) : '',
        quantity: Math.max(0, toDocumentNumber(item.quantity ?? 1)),
        price: Math.max(0, toDocumentNumber(item.price || 0)),
        discount: Math.max(0, toDocumentNumber(item.discount || 0)),
    } as TLineItem;
}

export function computeDocumentLineTotal(item: DocumentLineItem): number {
    return Math.round((Math.max(0, item.quantity * item.price - toDocumentNumber(item.discount)) + Number.EPSILON) * 100) / 100;
}

export function computeDocumentDeposit<TDeposit extends DocumentDeposit>(
    subtotal: number,
    input?: DocumentDepositInput,
): TDeposit {
    if (!input?.enabled) {
        return {
            enabled: false,
            mode: 'fixed',
            value: 0,
            amount: 0,
        } as TDeposit;
    }

    const value = Math.max(0, toDocumentNumber(input.value));
    const mode = input.mode || 'fixed';
    const amount = mode === 'percentage'
        ? Math.max(0, subtotal * (value / 100))
        : Math.max(0, value);

    return {
        enabled: true,
        mode,
        value,
        amount,
    } as TDeposit;
}

export function normalizeDocumentPaymentPlan<TPaymentPlan extends DocumentPaymentPlan>(
    input?: TPaymentPlan,
): TPaymentPlan | undefined {
    if (!input?.enabled) return undefined;

    return {
        enabled: true,
        installments: (input.installments || []).map(item => ({
            ...item,
            label: String(item.label || 'Installment').trim() || 'Installment',
            dueDate: item.dueDate || undefined,
            amount: Math.max(0, toDocumentNumber(item.amount || 0)),
            percentage: Math.max(0, toDocumentNumber(item.percentage || 0)),
            status: item.status || 'pending',
        })),
    } as TPaymentPlan;
}

export function buildDocumentAccounting<
    TLineItem extends DocumentLineItem,
    TDeposit extends DocumentDeposit,
    TPaymentPlan extends DocumentPaymentPlan,
>(input: {
    lineItems?: Array<Partial<TLineItem>>;
    deposit?: DocumentDepositInput;
    paymentPlan?: TPaymentPlan;
}) {
    const normalizedItems = (input.lineItems || [])
        .map(normalizeDocumentLineItem<TLineItem>)
        .filter(item => item.name && item.quantity >= 0);

    if (normalizedItems.length === 0) {
        throw new Error('At least one line item is required.');
    }

    const subtotal = normalizedItems.reduce((sum, item) => sum + computeDocumentLineTotal(item), 0);

    return {
        normalizedItems,
        subtotal,
        amountTotal: Number(subtotal.toFixed(2)),
        deposit: computeDocumentDeposit<TDeposit>(subtotal, input.deposit),
        paymentPlan: normalizeDocumentPaymentPlan<TPaymentPlan>(input.paymentPlan),
    };
}

export function amountFromInstallment(
    total: number,
    installment: { amount: number; percentage?: number },
): number {
    const explicitAmount = Math.max(0, toDocumentNumber(installment.amount || 0));
    if (explicitAmount > 0) return explicitAmount;

    const percent = Math.max(0, toDocumentNumber(installment.percentage || 0));
    if (percent <= 0) return 0;
    return Number((Math.max(0, total) * (percent / 100)).toFixed(2));
}

export function installmentLabel(
    installments: Array<{ label?: string }> | undefined,
    index: number,
    fallback = 'Installment',
): string {
    const raw = installments?.[index]?.label;
    return raw ? String(raw).trim() : `${fallback} ${index + 1}`;
}
